import { FieldPath, UseFormReturn } from 'react-hook-form';
import { ToggleField } from '@brighte/form-components';
import { HouseholdType, Period } from '@brighte/validation';
import { RepaymentField } from '@components/RepaymentField';
import { Stack } from '@spark-web/stack';
import { FinancialFieldTypes, HOUSEHOLD, LABELS, isFieldExists } from '@utils';

type Props = Pick<UseFormReturn<HouseholdType>, 'watch' | 'control' | 'setValue'> & {
  additionalFields: string[];
};

function Mortgage({ watch, control, setValue, additionalFields }: Props) {
  const { MORTGAGE_LABEL, MORTGAGE_SUBTITLE, MORTGAGE_REPAYMENT_HEADING } = HOUSEHOLD;

  const MORTGAGE_FIELD = 'applicant.expense.mortgage' as FieldPath<HouseholdType>;

  const [isMortgageNotApplicable, applicantMortgageAmount, applicantMortgagePeriod] = watch([
    `${MORTGAGE_FIELD}.isNotApplicable`,
    `${MORTGAGE_FIELD}.amount`,
    `${MORTGAGE_FIELD}.period`,
  ]);

  const hasIsNotApplicable = isFieldExists(additionalFields, `${MORTGAGE_FIELD}.isNotApplicable`);
  const handleMortgageToggle = (value: boolean) => {
    // value is isNotApplicable: true = "No" (not applicable), false = "Yes" (has mortgage)
    if (value) {
      setValue(`${MORTGAGE_FIELD}.amount` as FieldPath<HouseholdType>, 0);
      setValue(`${MORTGAGE_FIELD}.period` as FieldPath<HouseholdType>, Period.Monthly);
    }
  };
  // For products without isNotApplicable (e.g. BrighteFlex), just render amount fields directly
  if (!hasIsNotApplicable) {
    return (
      <RepaymentField
        control={control}
        periodFieldName={`${MORTGAGE_FIELD}.period` as FieldPath<HouseholdType>}
        amountFieldName={`${MORTGAGE_FIELD}.amount` as FieldPath<HouseholdType>}
        periodValue={applicantMortgagePeriod}
        amountValue={applicantMortgageAmount}
        financialFieldType={FinancialFieldTypes.MORTGAGE}
        disabled={isMortgageNotApplicable}
        gap="xxlarge"
        heading={MORTGAGE_REPAYMENT_HEADING}
      />
    );
  }

  return (
    <Stack gap="xxlarge">
      <ToggleField
        name={`${MORTGAGE_FIELD}.isNotApplicable` as FieldPath<HouseholdType>}
        label={MORTGAGE_LABEL}
        description={MORTGAGE_SUBTITLE}
        control={control}
        layout="inline"
        onChange={handleMortgageToggle}
        isInvertedValue
      />

      {!isMortgageNotApplicable && isMortgageNotApplicable !== undefined && (
        <RepaymentField
          control={control}
          periodFieldName={`${MORTGAGE_FIELD}.period` as FieldPath<HouseholdType>}
          amountFieldName={`${MORTGAGE_FIELD}.amount` as FieldPath<HouseholdType>}
          periodValue={applicantMortgagePeriod}
          amountValue={applicantMortgageAmount}
          financialFieldType={FinancialFieldTypes.MORTGAGE}
          heading={MORTGAGE_REPAYMENT_HEADING}
        />
      )}
    </Stack>
  );
}

export default Mortgage;
